import React, { useEffect, useMemo, useRef, useState } from 'react';
import '../../css/modal.css';
import { Followup } from '../../types/FollowUp';
import { Quotation } from '../../types/Quotation';
import { fetchFollowupsForAQuery } from '../../api/Followup';
import { fetchQuotationsForAQuery } from '../../api/Quotation';
import { formatDate } from '../../utils/date';

interface Props {
  queryId: number;
  onClose: () => void;
  onLog?: (followupId: number) => void;
}

type TimelineItem =
  | { kind: 'followup'; id: number; ts: number; data: Followup }
  | { kind: 'quotation'; id: number; ts: number; data: Quotation };

function parseFollowupTS(f: Followup): number {
  const datePart = String(f.date).includes('T') ? String(f.date).split('T')[0] : String(f.date).substring(0, 10);
  const timePart = (f.time || '').substring(0, 5) || '00:00';
  return new Date(`${datePart}T${timePart}`).getTime();
}

const QueryHistoryModal: React.FC<Props> = ({ queryId, onClose, onLog }) => {
  const hasFetched = useRef(false);
  const [followUps, setFollowUps] = useState<Followup[]>([]);
  const [quotes, setQuotes] = useState<Quotation[]>([]);

  useEffect(() => {
    if (hasFetched.current) return;
    hasFetched.current = true;
    (async () => {
      try {
        const [fu, qs] = await Promise.all([
          fetchFollowupsForAQuery(queryId),
          fetchQuotationsForAQuery(queryId),
        ]);
        setFollowUps(Array.isArray(fu) ? fu : []);
        setQuotes(Array.isArray(qs) ? qs : []);
      } catch (e) {
        setFollowUps([]);
        setQuotes([]);
      }
    })();
  }, [queryId]);

  const timeline = useMemo<TimelineItem[]>(() => {
    const a: TimelineItem[] = (followUps || []).map(f => ({ kind: 'followup', id: f.id as number, ts: parseFollowupTS(f), data: f }));
    const b: TimelineItem[] = (quotes || []).map(q => ({ kind: 'quotation', id: q.id as number, ts: new Date(String(q.createdAt)).getTime(), data: q }));
    return a.concat(b).sort((x, y) => y.ts - x.ts);
  }, [followUps, quotes]);

  const targetFollowupId = useMemo(() => {
    if (!followUps || followUps.length === 0) return null;
    const pending = followUps.find(f => !f.outcome || String(f.outcome).trim() === '' || String(f.outcome).toLowerCase() === 'pending');
    return (pending?.id as number) || (followUps[0].id as number) || null;
  }, [followUps]);

  return (
    <div className="modal-backdrop">
      <div className="modal">
        <div className="modal-header" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <h2 style={{ marginRight: 'auto' }}>Query History</h2>
          {onLog && targetFollowupId && (
            <button className="secondary" onClick={() => onLog(targetFollowupId)}>Log Interaction</button>
          )}
          <button className="close-btn" onClick={onClose}>×</button>
        </div>

        {timeline.length === 0 ? (
          <p>No follow-ups or quotations yet.</p>
        ) : (
          <div className="timeline-container">
            {timeline.map((item) => (
              <div key={`${item.kind}-${item.id}`} className="timeline-item">
                <div className="timeline-dot" />
                <div className="timeline-content">
                  <div className="timeline-header" style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <strong>
                      {item.kind === 'followup'
                        ? `${formatDate((item.data as Followup).date)} — ${String((item.data as Followup).time || '').substring(0,5)}`
                        : `${formatDate((item.data as Quotation).createdAt || '')}`}
                    </strong>
                    <span style={{ padding: '2px 6px', borderRadius: 999, fontSize: 12, background: item.kind === 'followup' ? '#ecfeff' : '#f5f3ff', border: '1px solid #e5e7eb', color: '#374151' }}>
                      {item.kind === 'followup' ? 'Follow-up' : 'Quotation'}
                    </span>
                  </div>

                  {item.kind === 'followup' ? (
                    <>
                      <p><strong>Why:</strong> {(item.data as Followup).description}</p>
                      <p><strong>Notes:</strong> {(item.data as Followup).notes}</p>
                      <p><strong>Outcome:</strong> {(item.data as Followup).outcome}</p>
                    </>
                  ) : (
                    <>
                      <p><strong>Amount:</strong> {(item.data as Quotation).amount}</p>
                      <p><strong>Notes:</strong> {(item.data as Quotation).notes || '—'}</p>
                      {(item.data as Quotation).url && (
                        <a className="download-link" href={(item.data as Quotation).url} target="_blank" rel="noopener noreferrer">📎 Download File</a>
                      )}
                    </>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default QueryHistoryModal;

